import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { mediaUrl } from '../../utils/mediaUrl';
import { AdminSidebar } from './AdminDashboardPage';
import AdminHeader from './AdminHeader';
import '../../css/adminSlider.css';
import '../../css/adminReels.css';
import { Plus, Pencil, Trash2, Loader2, AlertCircle, Clapperboard, Upload, ImagePlus, X, Eye } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';

const emptyForm = {
  title: '',
  views: 0,
  order: 0,
  isActive: true,
};

const AdminReels = () => {
  const { confirm } = useToast();
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingReel, setEditingReel] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState(emptyForm);
  const [videoFile, setVideoFile] = useState(null);
  const [thumbFile, setThumbFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState('');
  const [thumbPreview, setThumbPreview] = useState('');

  const videoInputRef = useRef(null);
  const thumbInputRef = useRef(null);

  useEffect(() => {
    loadReels();
  }, []);

  // clean up any local object URLs so we don't leak memory
  useEffect(() => {
    return () => {
      if (videoPreview) URL.revokeObjectURL(videoPreview);
      if (thumbPreview) URL.revokeObjectURL(thumbPreview);
    };
  }, [videoPreview, thumbPreview]);

  const loadReels = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/reels');
      setReels(data);
    } catch (err) {
      setError('Failed to load reels');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      setError('Please select a valid video file');
      return;
    }
    setVideoFile(file);
    setVideoPreview(URL.createObjectURL(file));
  };

  const handleThumbChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }
    setThumbFile(file);
    setThumbPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editingReel && !videoFile) {
      setError('Please select a video to upload');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const body = new FormData();
      body.append('title', formData.title);
      body.append('order', formData.order);
      body.append('isActive', formData.isActive);
      if (videoFile) body.append('video', videoFile);
      if (thumbFile) body.append('thumbnail', thumbFile);

      if (editingReel) {
        await api.put(`/reels/${editingReel._id}`, body, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await api.post('/reels', body, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      await loadReels();
      closeModal();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save reel');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Delete this reel?',
      message: 'The video will be removed from the homepage reels.',
      confirmLabel: 'Delete'
    });
    if (!ok) return;
    try {
      await api.delete(`/reels/${id}`);
      await loadReels();
    } catch (err) {
      setError('Failed to delete reel');
      console.error(err);
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      await api.patch(`/reels/${id}`, { isActive: !currentStatus });
      await loadReels();
    } catch (err) {
      setError('Failed to update reel status');
      console.error(err);
    }
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setVideoFile(null);
    setThumbFile(null);
    setVideoPreview('');
    setThumbPreview('');
    setEditingReel(null);
    if (videoInputRef.current) videoInputRef.current.value = '';
    if (thumbInputRef.current) thumbInputRef.current.value = '';
  };

  const openModal = (reel = null) => {
    setError('');
    if (reel) {
      setFormData({
        title: reel.title || '',
        views: reel.views || 0,
        order: reel.order || 0,
        isActive: reel.isActive,
      });
      setVideoPreview(mediaUrl(reel.video));
      setThumbPreview(mediaUrl(reel.thumbnail));
      setVideoFile(null);
      setThumbFile(null);
      setEditingReel(reel);
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const playPreview = (event) => {
    const video = event.currentTarget.querySelector('video');
    if (!video) return;
    video.play().catch(() => {});
  };

  const pausePreview = (event) => {
    const video = event.currentTarget.querySelector('video');
    if (!video) return;
    video.pause();
    try {
      video.currentTime = Math.min(0.3, (video.duration || 1) / 4);
    } catch (err) {
      /* ignore */
    }
  };

  const dragOn = (e) => e.currentTarget.classList.add('is-drag');
  const dragOff = (e) => e.currentTarget.classList.remove('is-drag');

  const activeCount = reels.filter((reel) => reel.isActive).length;

  const pageHeading = (withAction) => (
    <div className="asl-heading">
      <div>
        <h1 className="asl-title">Reels</h1>
        <p className="asl-subtitle">Portrait videos on the homepage. Hover a card to preview it.</p>
      </div>
      {withAction && (
        <div className="asl-heading-actions">
          <button type="button" className="asl-btn-primary" onClick={() => openModal()}>
            <Plus size={16} /> Add New Reel
          </button>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="admin-layout">
        <AdminSidebar />
        <div className="admin-content">
          <AdminHeader title="Reels" />
          <div className="asl-page">
            {pageHeading(false)}
            <div className="asl-state">
              <Loader2 size={26} className="asl-spinner" />
              <p className="asl-state-text">Loading reels...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <AdminHeader title="Reels" />
        <div className="asl-page">
          {pageHeading(true)}

          {error && (
            <div className="asl-error-message" role="alert">
              <AlertCircle size={16} /> {error}
            </div>
          )}

          {reels.length > 0 && (
            <div className="arl-stats">
              <div>
                <span>In the library</span>
                <strong>{reels.length}</strong>
              </div>
              <div>
                <span>On the homepage</span>
                <strong>{activeCount}</strong>
              </div>
              <div>
                <span>Hidden</span>
                <strong>{reels.length - activeCount}</strong>
              </div>
            </div>
          )}

          {!reels || reels.length === 0 ? (
            <div className="asl-state asl-state--empty">
              <span className="asl-state-icon"><Clapperboard size={20} /></span>
              <p className="asl-state-title">No reels uploaded yet</p>
              <p className="asl-state-text">Upload a short portrait video. It appears in the homepage carousel while it is active.</p>
              <button type="button" className="asl-btn-primary" onClick={() => openModal()}>
                <Plus size={16} /> Add First Reel
              </button>
            </div>
          ) : (
            <div className="arl-grid">
              {reels.map((reel) => (
                <article
                  key={reel._id}
                  className={`arl-card ${reel.isActive ? '' : 'is-hidden'}`}
                  onMouseEnter={playPreview}
                  onMouseLeave={pausePreview}
                >
                  <div className="arl-stage">
                    <video
                      src={mediaUrl(reel.video)}
                      poster={reel.thumbnail ? mediaUrl(reel.thumbnail) : undefined}
                      muted
                      playsInline
                      loop
                      preload="metadata"
                    />
                    <span className="arl-order">#{reel.order ?? 0}</span>
                    <button
                      type="button"
                      className={`arl-status ${reel.isActive ? 'is-on' : 'is-off'}`}
                      onClick={() => toggleStatus(reel._id, reel.isActive)}
                      title={reel.isActive ? 'Hide from the homepage' : 'Show on the homepage'}
                    >
                      {reel.isActive ? 'Live' : 'Hidden'}
                    </button>
                  </div>
                  <div className="arl-body">
                    <h2>{reel.title || 'Untitled reel'}</h2>
                    <p><Eye size={13} /> {(reel.views || 0).toLocaleString('en-IN')} views</p>
                    <div className="arl-actions">
                      <button type="button" onClick={() => openModal(reel)}>
                        <Pencil size={14} /> Edit
                      </button>
                      <button type="button" className="is-danger" onClick={() => handleDelete(reel._id)}>
                        <Trash2 size={14} /> Delete
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        {showModal && (
          <div className="asl-modal-overlay" onClick={closeModal}>
            <div className="asl-modal arl-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <div className="asl-modal-head">
                <h2>{editingReel ? 'Edit Reel' : 'Add New Reel'}</h2>
                <button type="button" className="asl-modal-close" onClick={closeModal} aria-label="Close">
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="asl-form">
                <div className="asl-modal-body arl-editor">
                  <div className="arl-phone">
                    {videoPreview ? (
                      <video src={videoPreview} poster={thumbPreview || undefined} muted playsInline loop autoPlay />
                    ) : (
                      <div className="arl-phone-empty">
                        <Clapperboard size={22} />
                        <span>Video preview</span>
                      </div>
                    )}
                  </div>

                  <div className="arl-fields">
                    <div className="asl-form-group">
                      <label htmlFor="reel-title">Title</label>
                      <input
                        id="reel-title"
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="Shown with the reel"
                      />
                    </div>

                    <div className="asl-form-group">
                      <label>Video {editingReel ? '(leave empty to keep the current file)' : ''}</label>
                      <div className="asl-dropzone" onDragEnter={dragOn} onDragLeave={dragOff} onDrop={dragOff}>
                        <input
                          type="file"
                          accept="video/*"
                          ref={videoInputRef}
                          onChange={handleVideoChange}
                          className="asl-dropzone-input"
                          aria-label="Reel video"
                        />
                        <span className="asl-dropzone-icon"><Upload size={18} /></span>
                        <span className="asl-dropzone-text">
                          <span className="asl-dropzone-title">
                            {videoFile ? videoFile.name : <><em>Click to upload</em> or drag a video here</>}
                          </span>
                          <span className="asl-dropzone-hint">MP4, WebM or MOV, portrait</span>
                        </span>
                      </div>
                    </div>

                    <div className="asl-form-group">
                      <label>Cover image (optional)</label>
                      <div className="asl-dropzone" onDragEnter={dragOn} onDragLeave={dragOff} onDrop={dragOff}>
                        <input
                          type="file"
                          accept="image/*"
                          ref={thumbInputRef}
                          onChange={handleThumbChange}
                          className="asl-dropzone-input"
                          aria-label="Reel thumbnail"
                        />
                        <span className="asl-dropzone-icon"><ImagePlus size={18} /></span>
                        <span className="asl-dropzone-text">
                          <span className="asl-dropzone-title">
                            {thumbFile ? thumbFile.name : <><em>Click to upload</em> or drag an image here</>}
                          </span>
                          <span className="asl-dropzone-hint">Used before the video starts</span>
                        </span>
                      </div>
                    </div>

                    <div className="asl-form-row">
                      <div className="asl-form-group">
                        <label htmlFor="reel-order">Order</label>
                        <input
                          id="reel-order"
                          type="number"
                          value={formData.order}
                          onChange={(e) => setFormData({ ...formData, order: Number(e.target.value) })}
                        />
                      </div>
                      <div className="asl-form-group asl-checkbox-group">
                        <label>
                          <input
                            type="checkbox"
                            checked={formData.isActive}
                            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                          />
                          Show on homepage
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="asl-modal-actions">
                  <button type="button" className="asl-btn-cancel" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="asl-btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : editingReel ? 'Update Reel' : 'Add Reel'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReels;
